/**
 * Issue #172 — 员工表单纯校验（与后端 DTO 对齐，可在 vitest 直接单测）。
 *
 * 前端先行校验只为即时反馈；后端仍是唯一权威（手机号唯一性 / 角色限制等由服务端
 * 强校验并回 4xx）。这里覆盖 name/username/phone/password/jobTitle 的格式边界。
 */
import type { AssignableRole, EmployeeStatus } from './types';

export interface EmployeeFormValues {
  name: string;
  username: string;
  phone: string;
  password: string;
  avatarUrl: string;
  deptId: string;
  jobTitle: string;
  role: AssignableRole;
  status: EmployeeStatus;
}

export type EmployeeFormErrors = Partial<Record<keyof EmployeeFormValues, string>>;

export const CN_MOBILE_RE = /^1[3-9]\d{9}$/;
export const USERNAME_RE = /^[A-Za-z0-9_一-龥]+$/;
export const ASSIGNABLE_ROLES: AssignableRole[] = ['STAFF', 'MANAGER', 'ADMIN'];

/**
 * 管理员在「编辑员工」里**可赋值**的状态（与后端 EMPLOYEE_ASSIGNABLE_STATUS_VALUES 对齐）。
 *
 * Issue #6：不含 CANCELLED——注销是员工自助终态，必须与「撤销全部 refreshToken +
 * 递增 tokenVersion + 写审计」原子发生；后端 UpdateEmployeeDto 的 @IsIn 也拒收 CANCELLED，
 * 前端若把它放进下拉，管理员只会拿到一个 400。同 ASSIGNABLE_ROLES 不含 SUPER_ADMIN 的思路。
 */
export const EMPLOYEE_STATUSES: EmployeeStatus[] = ['ACTIVE', 'INACTIVE', 'SUSPENDED'];

/**
 * 员工列表**筛选**可选的状态（读路径，含 CANCELLED）。
 *
 * Issue #6：管理员必须能筛出「账户已注销」的员工——注销不写 deletedAt，记录保持可见。
 * 与 EMPLOYEE_STATUSES（写路径）刻意分离，参照 FILTER_ROLES / ASSIGNABLE_ROLES 的既有先例。
 */
export const EMPLOYEE_FILTER_STATUSES: EmployeeStatus[] = [
  'ACTIVE',
  'INACTIVE',
  'SUSPENDED',
  'CANCELLED',
];

/**
 * 校验员工表单。`mode==='create'` 时 phone 必填；初始密码**可留空**——留空时服务端使用
 * 系统默认密码（按 orgId 隔离），填写则按 8-64 位校验作为覆盖初始密码。`edit` 时 phone 可留空
 * （不改手机号），password 不在编辑表单里（改密走「重置密码」）。
 */
export function validateEmployeeForm(
  values: EmployeeFormValues,
  mode: 'create' | 'edit',
): EmployeeFormErrors {
  const errors: EmployeeFormErrors = {};
  const name = values.name.trim();
  if (name.length < 2 || name.length > 32) {
    errors.name = '姓名需 2-32 个字符。';
  }
  const username = values.username.trim();
  if (username.length > 0) {
    if (username.length < 3 || username.length > 32) {
      errors.username = '用户名需 3-32 个字符。';
    } else if (!USERNAME_RE.test(username)) {
      errors.username = '用户名仅允许字母、数字、下划线或中文。';
    }
  }
  const phone = values.phone.trim();
  if (mode === 'create') {
    if (!CN_MOBILE_RE.test(phone)) {
      errors.phone = '请输入有效的 11 位中国大陆手机号。';
    }
  } else if (phone.length > 0 && !CN_MOBILE_RE.test(phone)) {
    errors.phone = '请输入有效的 11 位中国大陆手机号（留空表示不修改）。';
  }
  if (mode === 'create') {
    // Issue #183 返修：初始密码可留空（服务端用系统默认密码）；非空时按 8-64 位校验。
    const pwd = values.password;
    if (pwd.length > 0 && (pwd.length < 8 || pwd.length > 64)) {
      errors.password = '初始密码需 8-64 个字符（留空则使用系统默认密码）。';
    }
  }
  if (values.jobTitle.trim().length > 64) {
    errors.jobTitle = '岗位名称最多 64 个字符。';
  }
  if (!ASSIGNABLE_ROLES.includes(values.role)) {
    errors.role = '角色不合法。';
  }
  // Issue #6：已注销员工的 status 不参与校验——它不是「非法状态」，而是**不可赋值**的终态。
  // 编辑弹窗对这类员工把状态渲染成只读文案，toUpdatePayload 也不会回传 status，
  // 因此管理员仍能正常修改其姓名 / 部门 / 岗位等档案字段（后端同样只锁 status）。
  if (
    mode === 'edit' &&
    !isCancelled(values.status) &&
    !EMPLOYEE_STATUSES.includes(values.status)
  ) {
    errors.status = '状态不合法。';
  }
  return errors;
}

/** Issue #6：该员工是否已自助注销（终态，状态不可再改）。 */
export function isCancelled(status: EmployeeStatus): boolean {
  return status === 'CANCELLED';
}

/** 把表单值收敛成 create 请求体（去空白，可选字段空则省略）。 */
export function toCreatePayload(values: EmployeeFormValues) {
  return {
    name: values.name.trim(),
    phone: values.phone.trim(),
    // 留空则省略，服务端落到系统默认密码；填写则作为覆盖初始密码。
    password: values.password ? values.password : undefined,
    username: values.username.trim() || undefined,
    avatarUrl: values.avatarUrl || undefined,
    deptId: values.deptId || undefined,
    jobTitle: values.jobTitle.trim() || undefined,
    role: values.role,
  };
}

/**
 * 把表单值收敛成 edit 请求体（phone 留空表示不改；avatar/dept 同理）。
 *
 * Issue #6：**已注销员工不回传 status**。后端的可赋值集不含 CANCELLED，若这里照旧把
 * 表单里的 'CANCELLED' 原样发回，管理员哪怕只想改个岗位也会被 DTO 打回 400。省略该字段
 * 后请求走 PATCH 语义（不传即不改），档案字段照常可编辑，注销终态保持不变。
 */
export function toUpdatePayload(values: EmployeeFormValues) {
  const phone = values.phone.trim();
  return {
    name: values.name.trim(),
    username: values.username.trim() || undefined,
    phone: phone.length > 0 ? phone : undefined,
    avatarUrl: values.avatarUrl || undefined,
    deptId: values.deptId || undefined,
    jobTitle: values.jobTitle.trim() || undefined,
    role: values.role,
    status: isCancelled(values.status) ? undefined : values.status,
  };
}
